import { inject, Injectable } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { AuthenticationService } from '@maersk-global/angular-shared-library';
import { catchError, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { Roles } from '../constants/roles-permissions';
import { DryUser, SyncAccessRequest } from '../models/payload';
import { RbacService } from '../services/rbac.service';
import { SharedDataService } from '../services/shared-data.service';
import { decodeIdToken } from '../utils/common-utils';

/** Runs sync-access once per app session; resolver, terms guard and terms route all share this result. */
@Injectable({ providedIn: 'root' })
export class UserSyncService {
  private result$?: Observable<DryUser>;

  load(): Observable<DryUser> {
    return (this.result$ ??= loadUserData().pipe(
      // Don't cache fallback/error results (no userId); next load() retries sync-access.
      tap((user) => {
        if (!user.userId) {
          this.result$ = undefined;
        }
      }),
      shareReplay(1)
    ));
  }
}

export const UserDataResolver: ResolveFn<DryUser> = () => inject(UserSyncService).load();

const loadUserData = (): Observable<DryUser> => {
  const sharedService = inject(SharedDataService);
  const rbacService = inject(RbacService);
  const authenticationService = inject(AuthenticationService);
  const router = inject(Router);

  return authenticationService.user$.pipe(
    take(1),
    switchMap((user) => {
      const idClaims = decodeIdToken(user.idToken ?? '');
      // custom business claims (e.g. customer_code) may live in the access token
      const accessClaims = decodeIdToken(user.accessToken ?? '');
      const claims = { ...accessClaims, ...idClaims };
      const existingState = rbacService.getCurrentState();

      // External users have customer_code in token; internal users use previously selected
      const customerNumber = claims.customer_code
        ?? existingState?.customerNumber
        ?? '';

      const isExternal = !!claims.customer_code;

      const request: SyncAccessRequest = {
        externalUserRef: claims.userUuid ?? '',
        email: claims.email ?? user.email ?? '',
        displayName: claims.name ?? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        customerNumber,
      };

      return rbacService.syncAccess(request).pipe(
        map((rbacState) => {
          const dryUser: DryUser = {
            userId: rbacState.userId,
            username: user.uniqueId,
            email: claims.email ?? user.email,
            isActive: true,
            isExternal,
            roles: [{ roleId: 0, roleName: rbacState.role, isActive: true, permissions: [] }],
            customers: [{
              customerId: 0,
              customerNumber: rbacState.customerNumber,
              customerName: rbacState.customerName,
              contactNumber: 0,
              emailAddress: claims.email ?? user.email ?? '',
              isActive: true
            }],
            frInformation: user
          };
          sharedService.setuserRoleCustomersData(dryUser);
          if (rbacState.role === Roles.ROLE_ADMIN) {
            sharedService.getCustomersList();
          }
          return dryUser;
        }),
        catchError(() => {
          // sync-access failed — build minimal DryUser from JWT so app still loads
          const fallback: DryUser = {
            username: user.uniqueId,
            email: claims.email ?? user.email,
            isActive: true,
            isExternal,
            roles: [],
            customers: customerNumber ? [{
              customerId: 0,
              customerNumber,
              customerName: '',
              contactNumber: 0,
              emailAddress: claims.email ?? user.email ?? '',
              isActive: true
            }] : [],
            frInformation: user
          };
          sharedService.setuserRoleCustomersData(fallback);
          return of(fallback);
        })
      );
    }),
    catchError(() => {
      router.navigate(['/error']);
      return of({} as DryUser);
    })
  );
};
